import React, { useCallback } from 'react'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import LesaOfferCardSkeleton from './Common/LesaOfferCardSkeleton'
import { ChatActions, MessageMetaContent, MessageMetaPayload } from 'api/support'
import Action, { ActionsList } from 'supportAssistant/components/Chat/MessageMeta/Action'
import FrontPageTileFlightOnlyPricing from 'components/OfferList/OfferListTiles/Support/FrontPageTileFlightOnlyPricing'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import { ImageParams } from 'components/Common/ResponsiveImage'
import styled from 'styled-components'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import TileInclusions from 'components/OfferList/OfferListTiles/Support/OfferTileInclusions'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { rem } from 'polished'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import { EmptyArray } from 'lib/array/arrayUtils'

interface Props {
  hideSelect?: boolean;
  actions?: MessageMetaContent[];
  payload?: MessageMetaPayload;
  offer: App.HotelOffer;
  handleAction: (message: string, meta: { key: ChatActions, payload: MessageMetaPayload }) => void;
}

const imageParams: ImageParams = {
  aspectRatio: '16:9',
  desktopWidth: '65vw',
  largeDesktopWidth: '630px',
  quality: 'good',
  width: '250px',
  mobileWidth: '100%',
}

const StyledDivWrapper = styled.div`
  display: grid;
  grid-template-areas: "images description price";
  grid-template-columns: 6fr 8fr 5fr;
  grid-column-gap: ${rem(16)};
  width: ${rem(720)};
  border: 1px solid ${({ theme }) => theme.palette.neutral.default.five};
  padding-right: ${rem(12)};

  ${mediaQueryDown.tablet} {
    width: ${rem(680)};
    grid-template-areas:
      "images description"
      "images price";
    grid-template-columns: 3fr 4fr;
  }

  ${mediaQueryDown.mobile} {
    grid-column-gap: ${rem(16)};
    grid-template-areas:
      "images"
      "description"
      "price";
    grid-template-columns: 1fr;
    width: ${rem(320)};
    padding: ${rem(12)};
  }
`

const StyledImageCarouselDiv = styled.div`
  grid-area: images;

  ${mediaQueryDown.mobile} {
    height: ${rem(180)};
  }
`
const StyledContentDiv = styled.div`
  grid-area: description;
  padding: ${rem(16)} 0;
`

const StyledPriceDiv = styled.div`
  grid-area: price;
  padding: ${rem(16)} 0;
`

export default function LesaHotelOfferCard({ hideSelect, offer, actions, handleAction, payload }: Props) {
  const offerUrl = useOfferUrl(offer, {})

  const onSelectOffer = useCallback(() =>
    handleAction?.('', { key: ChatActions.SELECT_OFFER, payload: { offerId: offer.id } }),
  [handleAction, offer.id])

  if (!offer) {
    return <LesaOfferCardSkeleton />
  }

  return (
    <>
      <StyledDivWrapper>
        <StyledImageCarouselDiv>
          <ImageCarousel
            images={offer.images}
            imageParams={imageParams}
            eagerLoadFirstImage
          />
        </StyledImageCarouselDiv>
        <StyledContentDiv>
          <Group direction="vertical" gap={8}>
            <OfferUrgencyLabels offer={offer} />
            <CaptionTextBlock variant="large">{offer.location}</CaptionTextBlock>
            <HeadingTextBlock variant="heading5">{offer.name}</HeadingTextBlock>
            <ProductTypeLabel productType="hotel" kind="plain" />
            <TileInclusions
              offerTitle={offer.property?.name ? offer.property.name : offer.name}
              offerURL={offerUrl}
              heading=""
              tileInclusionsList={offer.tileInclusions || EmptyArray}
              maxInclusionsCount={3}
              maxLineCount={3}
            />
          </Group>
        </StyledContentDiv>
        <StyledPriceDiv>
          <Group direction="vertical" gap={12} verticalAlign="space-between" fullHeight>
            <div>
              {offer.bundledWithFlightsOnly && <FrontPageTileFlightOnlyPricing offer={offer} />}
              {!offer.bundledWithFlightsOnly && offer.lowestPricePackage &&
                <OfferTilePricing
                  offer={offer}
                  pkg={offer.lowestPricePackage}
                  offerUrl={offerUrl}
                  desktopAlign="start"
              />}
            </div>
            <Group gap={4} horizontalAlign="stretch" direction="horizontal-reverse" tabletDirection="horizontal-reverse" desktopDirection="vertical">
              {!hideSelect && <TextButton onClick={onSelectOffer} fit="full-width" size="medium" kind="primary">
                Ask about this offer
              </TextButton>}
              <TextButton to={offerUrl} target="_blank" fit="full-width" size="medium" kind="secondary">
                View details
              </TextButton>
            </Group>
          </Group>
        </StyledPriceDiv>
      </StyledDivWrapper>
      {!!actions?.length && <ActionsList>
        {actions?.map((action) => (
          <Action
            key={action.action?.key}
            action={action}
            payload={payload}
            handleAction={(text, meta) => handleAction(text, { ...meta, orderType: 'hotel' })}
          />
        ))}
      </ActionsList>}
    </>
  )
}
